// @ts-nocheck
/**
 * Category taxonomy for the mapping surfaces: which categories a row can be
 * moved to, how rows group and order, and how a category badge is coloured.
 *
 * Every existing tool maps into the standard categories hardcoded in
 * categoryColors.ts. They mount no provider, so they read DEFAULT_TAXONOMY,
 * which hands back those very constants and helpers (same references, so the
 * rendered output is what it was when the cards imported them directly).
 *
 * The Databook tool maps into a client's own category list, served on the
 * session snapshot as `taxonomy`:
 *
 *   { id, pnl: [{ name, section, group, sub, standard }],
 *         bs:  [{ name, side,    group, sub, standard }] }
 *
 * Each entry is one selectable category, in display order. `name` is the
 * string stored on mapping accounts (and sent in the mapping PATCH), `group`
 * is the heading its rows sit under (groups show in order of first
 * appearance), and `standard` is the nearest standard category, used only to
 * borrow that category's gradient. TaxonomyProvider turns that list into the
 * same interface the default exposes.
 */
import React, { createContext, useContext, useMemo } from 'react';

import {
  SELECTABLE_CATEGORY_ORDER,
  PARENT_ORDER,
  categoryStyle,
  parentCategory,
  leafOrderIndex,
  BS_CATEGORY_ORDER,
  SELECTABLE_BS_CATEGORY_ORDER,
  bsCategoryStyle,
} from './categoryColors';

export const DEFAULT_TAXONOMY = {
  // Income statement (FinalMappingCard).
  pnl: {
    // Categories offered in a row's dropdown, in order.
    selectable: SELECTABLE_CATEGORY_ORDER,
    // Category -> the group heading its rows sit under.
    parentOf: parentCategory,
    // Group headings, in display order.
    parentOrder: PARENT_ORDER,
    // Sort key that keeps a group's rows in category order.
    leafOrderIndex,
    // Category -> badge / dropdown gradient.
    style: categoryStyle,
  },
  // Balance sheet (FinalMappingCardBS).
  bs: {
    // Group headings, in display order.
    order: BS_CATEGORY_ORDER,
    selectable: SELECTABLE_BS_CATEGORY_ORDER,
    // Category -> group heading. Each standard bucket is its own group.
    groupOf: (category) => category,
    style: bsCategoryStyle,
  },
  // Badge gradient where the statement is not known to the renderer (the
  // tie-out ledger, the classify tool card).
  badgeStyle: categoryStyle,
};

const TaxonomyContext = createContext(DEFAULT_TAXONOMY);

export const useTaxonomy = () => useContext(TaxonomyContext);

const indexByName = (entries) => {
  const byName = new Map();
  entries.forEach((entry, index) => {
    if (!byName.has(entry.name)) byName.set(entry.name, { ...entry, index });
  });
  return byName;
};

const headingOf = (entry) => entry.group || entry.name;

const headingsInOrder = (byName) =>
  Array.from(new Set(Array.from(byName.values(), headingOf)));

/**
 * Build the context value from a snapshot `taxonomy`. A category the list
 * does not know (Uncategorized, or a name from an older pack) falls back to
 * what the standard helpers do with an unknown name: it groups under itself
 * and takes the standard gradient for that name, grey when there is none.
 *
 * A missing taxonomy yields EMPTY option lists, not the standard ones: a row
 * then offers only its current category, so nothing the client's template
 * has no line for can be picked while the list is still loading.
 */
export function buildTaxonomy(taxonomy) {
  const named = (list) =>
    (Array.isArray(list) ? list : []).filter((entry) => entry && entry.name);
  const pnl = indexByName(named(taxonomy?.pnl));
  const bs = indexByName(named(taxonomy?.bs));

  const pnlStyle = (category) => {
    const entry = pnl.get(category);
    return categoryStyle(entry ? entry.standard : category);
  };
  const bsStyle = (category) => {
    const entry = bs.get(category);
    return bsCategoryStyle(entry ? entry.standard : category);
  };

  return {
    pnl: {
      selectable: Array.from(pnl.keys()),
      parentOf: (category) => {
        if (!category) return 'Uncategorized';
        const entry = pnl.get(category);
        return entry ? headingOf(entry) : category;
      },
      parentOrder: headingsInOrder(pnl),
      leafOrderIndex: (category) =>
        pnl.get(category)?.index ?? Number.MAX_SAFE_INTEGER,
      style: pnlStyle,
    },
    bs: {
      order: headingsInOrder(bs),
      selectable: Array.from(bs.keys()),
      groupOf: (category) => {
        const entry = bs.get(category);
        return entry ? headingOf(entry) : category;
      },
      style: bsStyle,
    },
    badgeStyle: (category) =>
      !pnl.has(category) && bs.has(category)
        ? bsStyle(category)
        : pnlStyle(category),
  };
}

export const TaxonomyProvider = ({ taxonomy, children }) => {
  const value = useMemo(() => buildTaxonomy(taxonomy), [taxonomy]);
  return (
    <TaxonomyContext.Provider value={value}>{children}</TaxonomyContext.Provider>
  );
};
